import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ListView } from '@heroui-pro/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    isFlaggedRow,
    type ImportRow,
    type ImportRowEditHandler,
    type ImportRowExclusionHandlers,
} from '../../importRows';
import { GroupMarker } from '../groupMarker';
import { MatchChip } from '../matchChip';
import { NoteCell } from '../noteCell';
import { RowEditButton } from '../rowEdit';
import { RowExclusionButton } from '../rowExclusion';
import { StatusChip } from '../statusChip';
import { ImportCardFigures } from './ImportCardFigures';

type ImportCardProps = ImportRowExclusionHandlers &
    ImportRowEditHandler & {
        /** The row's key in the list: its row number (the list sets it). */
        id: number;
        row: ImportRow;
    };

/**
 * One row of the file as a card, for a phone: the table's fields stacked
 * top to bottom.
 *
 * 1. The row number and the status chip, with the row's Edit and Exclude /
 *    Include at the end of the line.
 * 2. The name, wrapping rather than truncating.
 * 3. The SKU with its group marker, and the attributes.
 * 4. Quantity, cost and sale price, each with its label (`ImportCardFigures`).
 * 5. The match chip (none on an excluded row).
 * 6. The note, wrapping: no pointer hovers a title on a phone. A flagged
 *    row's note ends with Fix (`isFlaggedRow`), which opens the drawer as
 *    Edit and the card's press do.
 *
 * An excluded row reads muted, as in the table. The content sits directly in
 * the list item rather than in `ListView.ItemContent`, whose rule colours
 * every icon inside it muted (the note's danger icon, the chips' icons).
 */
export const ImportCard: React.FC<ImportCardProps> = ({
    id,
    row,
    onEditRow,
    onExcludeRow,
    onIncludeRow,
}) => {
    const { t } = useTranslation('pages');
    const mutedWhenExcluded = row.isExcluded && 'text-muted';

    return (
        <ListView.Item
            id={id}
            textValue={t('catalogImport.products.card.label', {
                row: row.rowNumber,
                name: row.name,
            })}
            {...getNodeTestIdProp(`catalog-import-card-${row.rowNumber}`)}
        >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex items-center gap-2">
                    <span
                        className="text-sm tabular-nums text-muted"
                        {...getNodeTestIdProp(
                            `catalog-import-row-number-${row.rowNumber}`,
                        )}
                    >
                        {t('catalogImport.products.card.row', {
                            row: row.rowNumber,
                        })}
                    </span>
                    <StatusChip rowNumber={row.rowNumber} status={row.status} />
                    <span className="ms-auto flex items-center gap-1">
                        <RowEditButton
                            onEditRow={onEditRow}
                            rowNumber={row.rowNumber}
                        />
                        <RowExclusionButton
                            isExcluded={row.isExcluded}
                            onExcludeRow={onExcludeRow}
                            onIncludeRow={onIncludeRow}
                            rowNumber={row.rowNumber}
                        />
                    </span>
                </div>
                <span
                    className={cn(
                        'break-words text-sm font-medium',
                        mutedWhenExcluded,
                    )}
                    {...getNodeTestIdProp(
                        `catalog-import-row-name-${row.rowNumber}`,
                    )}
                >
                    {row.name}
                </span>
                <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
                    <span
                        className="break-all text-muted"
                        {...getNodeTestIdProp(
                            `catalog-import-row-sku-${row.rowNumber}`,
                        )}
                    >
                        {row.sku}
                    </span>
                    {row.groupSize === null ? null : (
                        <GroupMarker
                            groupSize={row.groupSize}
                            rowNumber={row.rowNumber}
                            sku={row.sku}
                        />
                    )}
                    {row.attributes === '' ? null : (
                        <span
                            className={cn(
                                'min-w-0 break-words',
                                mutedWhenExcluded,
                            )}
                            {...getNodeTestIdProp(
                                `catalog-import-row-attributes-${row.rowNumber}`,
                            )}
                        >
                            {row.attributes}
                        </span>
                    )}
                </div>
                <ImportCardFigures row={row} />
                {row.match === null ? null : (
                    <span className="self-start">
                        <MatchChip
                            match={row.match}
                            rowNumber={row.rowNumber}
                        />
                    </span>
                )}
                {row.note === null ? null : (
                    <NoteCell
                        wraps
                        isMuted={row.isExcluded}
                        note={row.note}
                        onFix={isFlaggedRow(row) ? onEditRow : undefined}
                        rowNumber={row.rowNumber}
                    />
                )}
            </div>
        </ListView.Item>
    );
};
